import React from "react";
import Slider from "react-slick";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

import MHButton from "../components/Common/Button/MHButton";

import { ReactComponent as LeftBtn } from "../static/svg/left-btn.svg";
import { ReactComponent as RightBtn } from "../static/svg/right-btn.svg";

import useMediaQuery from "@mui/material/useMediaQuery";
import { styled, useTheme } from "@mui/material/styles";

import useDialog from "../hooks/use-dialog";

import { useHistory } from "react-router-dom";
import useTitle from "../hooks/use-title";

import { formatAmount } from "../utils/utils";
import { InstituteGrid } from "../components/SubComponent/InstituteGrid";
import InstituteApproachSection from "../components/SubComponent/InstituteApproachSection";
import MHSubscribe from "../components/Layout/MHSubscribe";
import MHFooter from "../components/Layout/MHFooter";

type ArrowProps = {
  onClick?: (e: React.MouseEvent) => void;
};

function SampleNextArrow(props: ArrowProps) {
  return (
    <Box className="absolute top-[30%] z-10 lg:-right-8 md:-right-6">
      <RightBtn className="cursor-pointer" onClick={props.onClick} />
    </Box>
  );
}

function SamplePrevArrow(props: ArrowProps) {
  return (
    <Box className="absolute top-[30%] z-10 -left-8">
      <LeftBtn className="cursor-pointer" onClick={props.onClick} />
    </Box>
  );
}

const CONTENT = [
  {
    imageSrc:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-5.jpg",
    title: "Increase their visibility and attract more families.",
    description:
      "Learn proven strategies to stand out, gain trust, and enroll more children.",
  },
  {
    imageSrc:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-6.jpg",
    title: "Build a profitable and sustainable childcare business.",
    description:
      "Master financial planning, pricing, and growth strategies for long-term success in childcare.",
  },
  {
    imageSrc:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-7.jpg",
    title: "Deliver exceptional care while improving operations.",
    description:
      "Enhance learning environments, streamline processes, and provide top-tier childcare services efficiently.",
  },
  // {
  //   imageSrc:
  //     "https://mh-resources-production.s3.us-west-1.amazonaws.com/Care+Support2.png",
  //   title: "24/7 Care Concierge Service",
  //   description:
  //     "As a member of Caring Blocks, you'll have access to our 24/7 care concierge service. Whether you need support with your job or have a concern that needs addressing, we're here to help at any time, day or night.",
  // },
  // {
  //   imageSrc:
  //     "https://mh-resources-production.s3.us-west-1.amazonaws.com/16.png",
  //   title: "Safe and Trusted Work Environment",
  //   description:
  //     "We prioritize your safety and well-being. All families you are matched with have undergone thorough vetting, and we ensure a respectful and professional work environment. Your privacy and security are of utmost importance to us.",
  // },
];

const NOTES = [
  {
    label: "Informality & Lack of Protections",
    text: "Most care workers operate in informal settings with little to no legal protection, social security, or job stability. Without regulation, many face low wages, long hours, and exploitative conditions, with no access to healthcare or retirement benefits.",
  },
  {
    label: "Lack of Standardized Training & Certification",
    text: "There is no widely recognized standard for training and certifying care workers in many African countries, leading to inconsistencies in quality and professionalism. This prevents workers from advancing in their careers and keeps care work stigmatized as “unskilled labor” rather than a respected profession.",
  },
  {
    label: "Low Pay & Economic Insecurity",
    text: "Care work is among the lowest-paid sectors, despite its critical importance. Many caregivers earn below living wages, reinforcing economic inequalities—particularly for women, who dominate the sector.",
  },
  {
    label: "Limited Career Progression",
    text: "Care work should be a pathway to economic mobility, but without clear job ladders, ongoing training, or investment, care workers remain stuck in stagnant roles with no opportunity for advancement.",
  },
];

const APPROACH = [
  {
    label: "Formalize Care Work",
    text: "We advocate for national and regional policies that recognize care as essential labor, ensuring that care workers receive legal protections, contracts, fair wages, and access to social security.",
  },
  {
    label: "Establish Training & Certification Programs",
    text: "We are developing standardized, high-quality training and certification programs that equip care workers with skills, career pathways, and professional credentials—setting a new benchmark for excellence in caregiving.",
  },
  {
    label: "Improve Wages & Working Conditions",
    text: "We work with policymakers, businesses, and labor organizations to push for living wages, benefits, and dignified working conditions for all care workers.",
  },
  {
    label: "Integrate Care Into National Workforce Policies",
    text: "We advocate for the inclusion of care work in economic development plans—ensuring that governments invest in childcare, eldercare, and home-based care industries as key drivers of job creation.",
  },
  {
    label: "Build Employer-Sponsored Care Models",
    text: "We partner with companies to develop employer-supported childcare and eldercare programs, ensuring care workers are fairly compensated and supported in structured work environments.",
  },
  {
    label: "Create Career Progression & Economic Mobility",
    text: "We envision a career pathway for care workers—from entry-level caregivers to supervisors, trainers, and care entrepreneurs. By investing in skills development, we can ensure care work becomes a respected and financially viable career choice.",
  },
];

const CareWorkForcePage = () => {
  const {
    openDialog: open,
    handleOpenDialog: handleOpen,
    handleCloseDialog,
  } = useDialog();

  const handleClose = () => {
    handleCloseDialog();
  };

  const trainingCost = process.env.REACT_APP_CHILCARE_LAB_TRAINING_COST;

  const settings = {
    centerMode: true,
    centerPadding: "0px",
    dots: false,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    speed: 500,
    autoplay: false,
    // autoplaySpeed: 1400,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
  };

  const theme = useTheme();
  const matchesMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const history = useHistory();

  const handleCareRoute = () => {
    window.open("https://motherhonestly.typeform.com/cbcaregivers");
  };

  useTitle("For Women Health");

  return (
    <React.Fragment>
      <Grid container direction={{ xs: "column-reverse", md: "row" }} mb={4}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              "& > img": {
                height: 600,
                width: "100%",
                objectPosition: "center",
                objectFit: "cover",
              },
            }}
          >
            <img
              src="https://mh-resources-production.s3.us-west-1.amazonaws.com/The+Backbone+of+Society%2C+Yet+Invisible+in+the+Economy.jpg"
              // src="https://mh-resources-production.s3.us-west-1.amazonaws.com/Enhance+Productivity.png"
              alt="Banner"
              width="700"
              draggable={false}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} bgcolor="#F3F4F6">
          <Stack
            height={600}
            justifyContent="center"
            alignItems="center"
            px={{ xs: 2, md: 10 }}
            sx={{
              "& > p": {
                textAlign: "center",
                pointerEvents: "none",
              },
            }}
          >
            <Typography
              variant="body1"
              fontFamily="Area-Extended"
              textTransform="uppercase"
              color="#194049"
              fontSize={10}
              letterSpacing="0.1em"
              lineHeight="122%"
              width="60%"
              sx={{
                mb: 3,
              }}
              paragraph
            >
              Care Workforce
            </Typography>

            <Typography
              variant="h1"
              fontSize={{ xs: "30px", sm: "35px", md: "38px" }}
              textTransform="capitalize"
              width="90%"
              sx={{
                mb: 4,
              }}
              paragraph
            >
              The Backbone of Society, Yet Invisible in the Economy
            </Typography>

            <Typography
              variant="body1"
              align="center"
              color="#194049"
              width="85%"
              sx={{
                mb: 7,
              }}
              paragraph
            >
              Across Africa, millions of women and men serve as{" "}
              <b className=" font-areaBlack">
                nannies, domestic workers, caregivers, nurses, childcare
                providers, eldercare workers, and health aides
              </b>
              —yet their work remains undervalued, unregulated, and largely{" "}
              <b className=" font-areaBlack">
                invisible in formal labor structures.
              </b>
            </Typography>

            {/* <MHButton
              sx={{
                width: "fit-content",
              }}
              // onClick={handleCareRoute}
              onClick={() => {
                const element = document.getElementById("childcareLabReg");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              Start Your Application
            </MHButton> */}
          </Stack>
        </Grid>
      </Grid>

      <Box
        width={{ xs: "90%", md: "100%" }}
        mx="auto"
        pt={5}
        justifyContent="center"
        // sx={{ backgroundColor: "#f6f6f6" }}
        className="mx-auto place-content-center"
      >
        <Typography
          variant="body1"
          fontFamily="Area-Extended"
          textTransform="uppercase"
          color="#194049"
          fontSize={{ xs: "12px", sm: "14px" }}
          letterSpacing="0.1em"
          lineHeight="122%"
          // width="100%"
          sx={{
            mb: 5,
            textAlign: "center",
          }}
          paragraph
        >
          The Care Workforce: Professionalizing, Regulating, and Formalizing
          Care Work in Africa
        </Typography>

        <Typography
          variant="body1"
          align="center"
          color="#194049"
          width={{ xs: "90%", md: "70%" }}
          sx={{
            mb: 7,
            // px: { xs: 1, sm: 5 },
            mx: "auto",
          }}
          paragraph
        >
          <b className=" font-areaBlack">The care workforce </b>is the
          <b className=" font-areaBlack"> foundation of economic growth.</b> It
          enables parents to work, children to thrive, and aging populations to
          live with dignity. But despite its critical role, care work is often
          treated as informal, low-skilled, and poorly compensated labor—{" "}
          <b className=" font-areaBlack">
            trapping workers, mostly women, in cycles of economic insecurity
          </b>
          . If we are to close the care gap and{" "}
          <b className=" font-areaBlack">
            build a sustainable, gender-equitable economy,
          </b>{" "}
          we must professionalize, regulate, and formalize the care
          workforce—ensuring that care jobs are{" "}
          <b className=" font-areaBlack">
            dignified, well-paid, and properly integrated into Africa's labor
            market.
          </b>
        </Typography>
      </Box>
      <Box sx={{ backgroundColor: "#194049" }} p={3}>
        <Typography
          variant="h1"
          color="#F8F246"
          fontSize={{ xs: "26px", sm: "32px" }}
          lineHeight={{ xs: "143%", sm: "120.5%" }}
          letterSpacing={"-0.04em"}
          width={"80%"}
          className=" mx-auto text-center"
        >
          The Challenges Facing Africa's Care Workforce
        </Typography>

        <Grid container spacing={2} mt={5} className=" place-content-center">
          {NOTES.map((note, index) => (
            <Grid item xs={10} sm={5}>
              <Typography
                variant="body1"
                fontFamily="Area-Extended"
                textTransform="uppercase"
                color="#fff"
                fontSize={{ xs: "12px", sm: "14px" }}
                letterSpacing="0.1em"
                lineHeight="122%"
                // width="100%"
                sx={{
                  mb: 5,
                  textAlign: "center",
                }}
                paragraph
              >
                {note.label}
              </Typography>

              <Typography
                variant="body1"
                align="center"
                color="#fff"
                width="100%"
                sx={{
                  mb: 7,
                  px: { xs: 1, sm: 5 },
                }}
                paragraph
              >
                {note.text}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      <InstituteApproachSection
        header="Our Vision"
        text="A Thriving, Professionalized Care Workforce"
        content="At Caring Africa, we are committed to transforming the care workforce into a recognized, professionalized, and dignified sector of the economy. Through policy advocacy, workforce development, and industry partnerships, we are working to:"
        data={APPROACH}
      />

      <Box
        width={{ xs: "90%", sm: "100%" }}
        mx="auto"
        py={5}
        sx={{ backgroundColor: "#f6f6f6" }}
      >
        <Typography
          variant="h1"
          fontSize={{ xs: "30px", sm: "40px", md: "50px" }}
          textTransform="capitalize"
          align="center"
          sx={{
            mb: 4,
          }}
          paragraph
        >
          The Care Workforce is the Future of Work in Africa
        </Typography>
        <Typography
          variant="body1"
          color="primary.main"
          width={{ xs: "100%", md: "70%" }}
          fontSize="14px"
          lineHeight="185%"
          letterSpacing="0.02em"
          align="center"
          mx="auto"
          paragraph
        >
          Formalizing the care workforce is{" "}
          <b className=" font-areaBlack">
            not just about worker rights—it is an economic imperative.
          </b>{" "}
          Investing in the <b className=" font-areaBlack">care economy</b> will
          create
          <b className=" font-areaBlack"> millions of jobs,</b> improve{" "}
          <b className=" font-areaBlack">household incomes,</b> and drive{" "}
          <b className=" font-areaBlack">economic growth.</b>
        </Typography>

        {/* <MHButton
          sx={{
            width: "fit-content",
            display: "block",
            mx: "auto",
            mt: 5,
          }}
          onClick={handleCareRoute}
        >
          Start Your Application
        </MHButton> */}
      </Box>

      {/* <InstitutesHeroSlider
        data={CONTENT}
        header="Who Should Attend?"
        content={`Childcare providers, daycare owners, and educators who want to:`}
      /> */}

      <MHSubscribe />
      <MHFooter />
    </React.Fragment>
  );
};

export default CareWorkForcePage;
